import { LoadingOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import { Collapse, Spin, Tooltip } from 'antd';
import Link from 'next/link';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileDrop } from 'react-file-drop';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import AppreciateSvg from '../../../assets/icons/AppreciateSvg';
import CuripodAIStarsSvg from '../../../assets/icons/CuripodAIStarsSvg';
import EnterprisePlanIcon from '../../../assets/icons/EnterprisePlanIcon';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import SorrySvg from '../../../assets/icons/SorrySvg';
import TestPrepModalSvg from '../../../assets/icons/TestPrepModalSvg';
import theme from '../../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../../hooks/useAccessBadge';
import useAuthGuardPrompt from '../../../hooks/useAuthGuardPrompt';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useConsumeGeneratorAsNewPresentation from '../../../hooks/useConsumeGeneratorAsNewPresentation';
import { useDynamicLessonStateContext } from '../../../hooks/useCreateLessonState';
import { GeneratorCategory } from '../../../hooks/useDesignerLogic';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useGetGeneratorsByIdV2 } from '../../../hooks/useGetGeneratorsById';
import { useMetrics } from '../../../hooks/useMetrics';
import { newRelicNoticeError } from '../../../hooks/useNewRelic';
import usePaywallPrompt, { PaywallMetadata } from '../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../hooks/useRouterMatch';
import {
  LS_KEY_TEST_PREP_STATE,
  TestPrepAction,
  TestPrepState,
} from '../../../hooks/useTestPrepState';
import { useUploadPresentation } from '../../../hooks/useUploadPresentation';
import BackendService, {
  LessonAttachment,
} from '../../../services/backendService/backendService';
import { PromptInput } from '../../../services/backendService/types';
import {
  AiConsumerContent,
  GeneratorCoarseLicenseLevel,
} from '../../../services/TemplateService/Template.type';
import {
  generatorFormStateToUserInputFormData,
  MAX_LIMIT_LEARNING_OBJECTIVE,
  MAX_LIMIT_PROMPT,
} from '../../../utils/aiGeneratorUtils';
import { getFiles } from '../../../utils/fileDropUtils';
import { debounceEventIfNotTrusted } from '../../../utils/general';
import { isIos } from '../../../utils/navigator';
import { Input } from '../../_atoms/_Input';
import { Loader } from '../../_atoms/_Loader';
import { TextAreaV2 } from '../../_atoms/_TextArea/TextAreaV2';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../../_molecules/BaseModal';
import ShowLessonCreationProgressBar from '../../_molecules/ProgressBar/ShowLessonCreationProgressBar';
import {
  GeneratorFormState,
  UploadPassageInput,
  useGeneratorFormState,
} from '../../_organisms/DesignerTabPane/PromptsTab/GeneratorForm';
import { Slide } from '../../SlideComponents/slide.types';
import {
  CascadingFloatingSelect,
  TestPrepFloatingSelect,
} from './components/FloatingSelect';
import ProgressBarDynamicLesson from './components/ProgressBarDynamicLesson';
import { SubPageLayout } from './CreatePage';
import {
  getQuestionTypes,
  getQuestionTypes_templated,
  useAllTestsForTestGroup,
  useGetLessonIds,
  useGetLessonVariants,
  useGroupedStatesWithTests,
} from './TestPrep.Utils';

export type ConsumerGenerator = {
  id: string;
  tenantId: string;
  title?: string | null;
  description: string | null;
  lastPublished: number | null;
  categories: GeneratorCategory[];
  firstPublished: number | null;
  createdAt: number;
  modifiedAt: number;
  creatorId: string;
  creatorProfileId: string;
  creatorThumbnailId?: string | null;
  rounds: Slide[];
  language: string | null;
  icon?: ReactNode | null; // @deprecated
  coverImageId: string | null;
  coverImageAltText: string | null;
  downloadCount: number;
  tags: string[];
  grades: string[];
  subjects: string[];
  creatorName: string;
  playCount?: number;
  participantCount?: number;
  status: 'published' | 'rejected' | 'submitted' | 'unPublishedByOwner' | 'staging';
  requireRealNames?: boolean;
  adminTags?: string[]; // Merge with tags from description
  adminLanguage?: string;
  slides?: Slide[];
  roundIndexField?: string;
  slideIndexField?: string;
  aiContent?: AiConsumerContent;
  isGenerator?: boolean;
  learningStandards: Standard[];
  sampleLessons?: SampleLesson[];
};

export type Standard = {
  id: string;
  label: string;
  description: string;
};

export type SampleLesson = {
  id: string;
  inputs: PromptInput[];
  slides: Slide[];
  lessonAttachments?: LessonAttachment[];
};

export const TestPrepTab = ({
  testPrepState,
  dispatch,
}: {
  testPrepState: TestPrepState;
  dispatch: React.Dispatch<TestPrepAction>;
}) => {
  const { userAccessBadge, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const isInUSA = userAccessBadge?.organization?.country === 'US';
  const {
    selectedTest,
    selectedQuestionType,
    lessonVariant,
    isUpload,
    selectedPage,
    hasUploadOptions,
  } = testPrepState;

  const { tenantId } = useRouterMatch();

  const allLessonIdsForSelectedTest = useGetLessonIds({
    testId: selectedTest,
  });

  const { generators, isFetchingGenerators } = useGetGeneratorsByIdV2(
    allLessonIdsForSelectedTest,
  );

  const availableLessonIds = useGetLessonIds({
    testId: selectedTest,
    questionTypeId: selectedQuestionType,
    lessonVariantId: lessonVariant,
    isUpload,
  });

  const selectedGenerator = useCallback(() => {
    if (availableLessonIds?.length === 1) {
      const foundGenerator = generators?.find(
        generator => generator.id === availableLessonIds[0],
      );

      if (foundGenerator === undefined) {
        newRelicNoticeError('No generator found for test prep', {
          lessonIds: availableLessonIds,
          selectedTest,
          selectedQuestionType,
          lessonVariant,
          isUpload,
          generatorIdsFound: generators?.map(g => g.id),
        });
        return undefined;
      }
      return foundGenerator;
    }

    return undefined;
  }, [
    generators,
    availableLessonIds,
    selectedTest,
    selectedQuestionType,
    lessonVariant,
    isUpload,
  ]);
  const isLargerThan600 = useBreakPoint(600);
  const [showLoader, setShowLoader] = useState(false);

  const formProgress = useMemo(() => {
    if (selectedPage === 1) {
      // No subject selected, progress is 0%
      return 10;
    }
    if (selectedPage === 2) {
      return 50;
    }
    if (selectedPage === 3) {
      // on the generate page progress is 100%
      return 100;
    }
    return 0;
  }, [selectedPage]);

  const handleIsUploadChange = (isUpload: boolean) => {
    dispatch({ type: 'SET_IS_UPLOAD', payload: isUpload });
  };

  const handleLessonPageChange = (page: number) => {
    dispatch({ type: 'SET_SELECTED_PAGE', payload: page });
  };

  const { t } = useTranslate();

  return (
    <>
      {!showLoader && isLargerThan600 && (
        <ProgressBarDynamicLesson progress={formProgress} numberOfSteps={3} />
      )}
      {paywallComponent}

      <>
        {/*Select subject*/}
        {selectedPage === 1 && (
          <SubPageLayout
            pageTitle={t('TestPrepTab.PageTitle', 'Generate Your Test Prep')}
          >
            <GenerateTestPrepFirstForm
              testPrepState={testPrepState}
              dispatch={dispatch}
              tenantId={tenantId}
              isUsTeacher={isInUSA}
              availableGenerators={generators}
              userAccessBadge={userAccessBadge}
              generators={generators}
              promptPaywall={promptPaywall}
              isReady={isReady}
            />
          </SubPageLayout>
        )}
      </>

      {selectedPage === 2 && (
        <SubPageLayout pageTitle="Choose Your Content">
          <SelectUploadOrGenerate
            handleIsUploadChange={handleIsUploadChange}
            handleSelectedPageChange={handleLessonPageChange}
            hasUploadOption={!!hasUploadOptions}
          />
        </SubPageLayout>
      )}

      {selectedPage === 3 && (
        <>
          {/*Generate the lesson form */}
          <SubPageLayout
            pageTitle={
              showLoader
                ? ''
                : t('TestPrepTab.PageTitle.AFewMoreDetails', 'A few more details')
            }
          >
            <TabContainer>
              <GenerateTestPrepSecondForm
                selectedGenerator={selectedGenerator()}
                setShowLoader={setShowLoader}
                showLoader={showLoader}
                availableLessonIds={availableLessonIds}
                availableGenerators={generators}
                promptPaywall={promptPaywall}
                isReady={isReady}
                userAccessBadge={userAccessBadge}
                isFetchingGenerators={isFetchingGenerators}
                testPrepState={testPrepState}
                dispatch={dispatch}
              />
            </TabContainer>
          </SubPageLayout>
        </>
      )}
    </>
  );
};

const GenerateTestPrepFirstForm = ({
  testPrepState,
  dispatch,
  tenantId,
  isUsTeacher,
  availableGenerators,
  userAccessBadge,
  generators,
  promptPaywall,
  isReady,
}: {
  testPrepState: TestPrepState;
  dispatch: React.Dispatch<TestPrepAction>;
  tenantId: string;
  isUsTeacher: boolean;
  availableGenerators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
  generators: ConsumerGenerator[] | undefined;
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  isReady: boolean;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const isTemplatedTestPrep = useFeatureFlag('templated_customizable_lessons');
  const [showRequestTestModal, setShowRequestTestModal] = useState<string | undefined>(
    undefined,
  );
  const [isFormErrorVisible, setIsFormErrorVisible] = useState(false);
  const [testRequest, setTestRequest] = useState<{
    state: string | undefined;
    nameOfTest: string | undefined;
  }>({ state: undefined, nameOfTest: undefined });
  const {
    selectedState,
    selectedTest,
    selectedTestCategory,
    selectedTestCategoryDropdown,
    selectedQuestionType,
    selectedPage,
    hasUploadOptions,
  } = testPrepState;

  const { mutate: submit, isLoading, isSuccess } = useMutation<
    void,
    string,
    { state: string; nameOfTest: string }
  >({
    mutationFn: ({ state, nameOfTest }: { state: string; nameOfTest: string }) => {
      const testNameLocationAppended = `${nameOfTest} \n*Dropdown requested from:* ${showRequestTestModal}`;

      return BackendService.instance.testRequest.submit(
        state,
        testNameLocationAppended,
        tenantId,
      );
    },
  });

  const groupedStatesWithTests = useGroupedStatesWithTests();

  const handleSelectTest = (state: string, test?: string) => {
    setIsFormErrorVisible(false);
    dispatch({ type: 'SET_SELECTED_STATE', payload: state });
    if (test) dispatch({ type: 'SET_SELECTED_TEST_CATEGORY', payload: test });
  };

  const handleLessonPageChange = (page: number) => {
    dispatch({ type: 'SET_SELECTED_PAGE', payload: page });
  };

  const isDesktop = useBreakPoint(700);
  const isLargerThan850 = useBreakPoint(850);

  const handleSendTestRequest = () => {
    if (!testRequest.state || !testRequest.nameOfTest) return;
    metrics.logEvent('TestPrep.RequestModalSubmitted', {
      state: testRequest.state,
      nameOfTest: testRequest.nameOfTest,
    });
    submit({ state: testRequest.state, nameOfTest: testRequest.nameOfTest });
  };

  const handleSelectedCategoryDropdown = (value: string | undefined) => {
    setIsFormErrorVisible(false);
    dispatch({ type: 'SET_SELECTED_TEST_CATEGORY_DROPDOWN', payload: value });
  };

  const availableQuestionTypes = useMemo(() => {
    return isTemplatedTestPrep
      ? getQuestionTypes_templated({
          testId: selectedTest,
          generators: availableGenerators,
          userAccessBadge,
        })
      : getQuestionTypes({
          testId: selectedTest,
          generators: availableGenerators,
          userAccessBadge,
        });
  }, [isTemplatedTestPrep, selectedTest, availableGenerators, userAccessBadge]);

  const availableTests = useAllTestsForTestGroup(
    selectedState,
    selectedTestCategory,
    generators,
    userAccessBadge,
  );

  const handleShowRequestTestModal = (location: string) => {
    setShowRequestTestModal(location);
    metrics.logEvent('TestPrep.RequestModalOpened', { location });
  };

  return (
    <>
      <BaseModal
        visible={!!showRequestTestModal}
        onCancel={() => {
          metrics.logEvent('TestPrep.RequestModalClosed');
          setShowRequestTestModal(undefined);
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          {!isSuccess && (
            <>
              <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
                <TestPrepModalSvg />
              </IconWrapper>
              <h1 style={{ marginTop: 20, fontSize: 24 }}>
                Let us know what test you are looking for
              </h1>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 30,
                }}
              >
                <Input
                  style={{ width: '100%' }}
                  type="text"
                  borderLess
                  inputStyle={{
                    boxShadow: theme.effects.floatingCreatorElementShadow,
                    border: 'none',
                  }}
                  value={testRequest?.state}
                  label={t('State', 'State')}
                  placeholder={t('e.g. Texas', 'e.g. Texas')}
                  labelStyle={{
                    fontSize: '18px',
                    color: theme.colors.black,
                    fontWeight: 'bold',
                  }}
                  onChange={e =>
                    setTestRequest({
                      ...testRequest,
                      state: e.target.value,
                    })
                  }
                />
                <Input
                  style={{ width: '100%' }}
                  type="text"
                  borderLess
                  inputStyle={{
                    boxShadow: theme.effects.floatingCreatorElementShadow,
                    border: 'none',
                  }}
                  value={testRequest?.nameOfTest}
                  label={t('Test', 'Test')}
                  placeholder={t(
                    'e.g. STAAR Science - Short constructed response',
                    'e.g. STAAR Science - Short constructed response',
                  )}
                  labelStyle={{
                    fontSize: '18px',
                    color: theme.colors.black,
                    fontWeight: 'bold',
                  }}
                  onChange={e =>
                    setTestRequest({
                      ...testRequest,
                      nameOfTest: e.target.value,
                    })
                  }
                />
              </div>
              <Button
                styles={{ marginTop: 20 }}
                loading={isLoading}
                onClick={() => handleSendTestRequest()}
                disabled={!testRequest.state || !testRequest.nameOfTest}
              >
                <T keyName="Send Request" defaultValue="Send Request" />
              </Button>
            </>
          )}
          {isSuccess && (
            <>
              <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
                <AppreciateSvg />
              </IconWrapper>
              <h1 style={{ marginTop: 20, fontSize: 24 }}>Thanks for your request!</h1>
              <p style={{ margin: 20, fontSize: 18 }}>
                Our team is adding your request to our list. In the meantime, explore
                available{' '}
                <Link href={`/${tenantId}/create?lessonCategory=test_prep`} passHref>
                  <StyledLink
                    onClick={() => metrics.logEvent('TestPrep.ViewAllTestPrep.Clicked')}
                  >
                    <T
                      keyName="test prep lessons here"
                      defaultValue="test prep lessons here"
                    />
                  </StyledLink>
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </BaseModal>
      <FormContainer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              alignItems: isDesktop ? 'center' : 'start',
              gap: 20,
              flexDirection: isDesktop ? 'row' : 'column',
            }}
          >
            <CascadingFloatingSelect
              label={t('Test', 'Test')}
              width={isLargerThan850 ? undefined : '100%'}
              value={{ value: selectedState, subValue: selectedTestCategory }}
              options={groupedStatesWithTests}
              onSelect={(state, test) => handleSelectTest(state, test)}
              setSelectedCategory={handleSelectedCategoryDropdown}
              selectedCategory={selectedTestCategoryDropdown}
              displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
              handleShowRequestTestModal={(location: string) =>
                handleShowRequestTestModal(location)
              }
            />
          </div>
          <TestPrepFloatingSelect
            label={t('Subject', 'Subject')}
            width={isLargerThan850 ? undefined : '100%'}
            value={selectedTest}
            options={availableTests}
            onSelect={test => dispatch({ type: 'SET_SELECTED_TEST', payload: test })}
            displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
            handleShowRequestTestModal={(location: string) =>
              handleShowRequestTestModal(location)
            }
            promptPaywall={promptPaywall}
            userAccessBadge={userAccessBadge}
            isReady={isReady}
            disabled={!selectedState}
            testRequest={
              <span style={{ fontWeight: 'normal', color: theme.colors.greyDark }}>
                <T
                  keyName="Don't see your subject?"
                  defaultValue="Don't see your subject?"
                />{' '}
                <i
                  style={{
                    color: theme.colors.greyDark,
                    textDecoration: 'underline',
                  }}
                >
                  <T
                    keyName="Request your subject here."
                    defaultValue="Request your subject here."
                  />
                </i>
              </span>
            }
          />
          <TestPrepFloatingSelect
            label={t('Question Type', 'Question Type')}
            width={isLargerThan850 ? undefined : '100%'}
            value={selectedQuestionType}
            options={availableQuestionTypes}
            onSelect={questionType => {
              dispatch({ type: 'SET_SELECTED_QUESTION_TYPE', payload: questionType });
            }}
            displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
            handleShowRequestTestModal={(location: string) =>
              handleShowRequestTestModal(location)
            }
            promptPaywall={promptPaywall}
            userAccessBadge={userAccessBadge}
            isReady={isReady}
            disabled={!selectedState}
            testRequest={
              <span style={{ fontWeight: 'normal', color: theme.colors.greyDark }}>
                <T
                  keyName="Don't see the question type you're looking for?"
                  defaultValue="Don't see the question type you're looking for?"
                />{' '}
                <i
                  style={{
                    color: theme.colors.greyDark,
                    textDecoration: 'underline',
                  }}
                >
                  <T keyName="Request it here." defaultValue="Request it here." />
                </i>
              </span>
            }
          />
        </div>

        <div
          style={{
            margin: '20px auto 10px auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <OrangeButton
            type="primary"
            width={isDesktop ? '200px' : '100%'}
            onClick={() => {
              if (!selectedState || !selectedTest || !selectedQuestionType) {
                setIsFormErrorVisible(true);
                return;
              }

              metrics.logEvent('TestPrep.NextButtonClicked', {
                state: selectedState,
                test: selectedTest,
                questionType: selectedQuestionType,
                currentPage: 1,
              });

              const hasAccessToEnterprise =
                userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise;

              const isEnterpriseQuestionOptionSelected = availableQuestionTypes.find(
                q => q.value === selectedQuestionType,
              )?.isEnterprise;

              const isEnterpriseTestSelected = availableTests.find(
                t => t.value === selectedTest,
              )?.isEnterprise;

              if (
                (isEnterpriseQuestionOptionSelected || isEnterpriseTestSelected) &&
                !hasAccessToEnterprise
              ) {
                promptPaywall({
                  label: t('Upgrade account test prep', 'Upgrade account test prep'),
                  description: t(
                    'Access to ${selectedTest} ${selectedQuestionType}',
                    'Access to ${selectedTest} ${selectedQuestionType}',
                    {
                      selectedTest,
                      selectedQuestionType,
                    },
                  ),
                });
                return;
              }

              if (!hasUploadOptions) {
                //If there are no upload options then we skip page 2
                handleLessonPageChange(3);
                return;
              }

              handleLessonPageChange(selectedPage + 1);
            }}
            $disabled={false}
          >
            Next
          </OrangeButton>
        </div>
        {isFormErrorVisible && (
          <FormErrorText>
            <T
              keyName="Don't forget to select a test"
              defaultValue="Don't forget to select a test"
            />
          </FormErrorText>
        )}
      </FormContainer>
    </>
  );
};

const StyledLink = styled.a`
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;

const AnimatedButton = ({
  handleSelectedPageChange,
  handleSelectUpload,
  buttonText,
  isEnterprisePlan,
  disabled,
}: {
  handleSelectedPageChange: (page: number) => void;
  handleSelectUpload: () => void;
  buttonText: string;
  isEnterprisePlan?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const { userAccessBadge, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  return (
    <div style={{ position: 'relative' }}>
      <Tooltip
        title={!!disabled ? t('Coming Soon!', 'Coming Soon!') : ''}
        mouseEnterDelay={0.5}
      >
        <CategoryButton
          type="primary"
          width="200px"
          $isComingSoon={!!disabled}
          onClick={() => {
            if (disabled) {
              metrics.logEvent('TestPrep.DisabledButtonClicked');
              return;
            }
            if (!isReady) return;
            if (
              isEnterprisePlan &&
              userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
            ) {
              promptPaywall({
                label: t('Upgrade account test prep', 'Upgrade account test prep'),
                description: t(
                  'Use my own materials for test prep',
                  'Use my own materials for test prep',
                ),
              });
              return;
            } else {
              metrics.logEvent('TestPrep.ContentTypeButton.Clicked', {
                buttonText: buttonText,
                isEnterprisePlan: isEnterprisePlan,
              });
              handleSelectedPageChange(3);
              handleSelectUpload();
            }
          }}
        >
          {buttonText}
        </CategoryButton>
      </Tooltip>
      {paywallComponent}
      {isEnterprisePlan && (
        <PositioningWrapper>
          <TestprepPlanIcon
            hasAccessToGenerator={
              isEnterprisePlan &&
              userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
            }
            isEnterprisePlanGenerator={isEnterprisePlan}
            isPremiumPlanGenerator={false}
          />
        </PositioningWrapper>
      )}
    </div>
  );
};

export const TestprepPlanIcon = ({
  isPremiumPlanGenerator,
  isEnterprisePlanGenerator,
  hasAccessToGenerator,
}: {
  isPremiumPlanGenerator: boolean | null | undefined;
  isEnterprisePlanGenerator: boolean;
  hasAccessToGenerator: boolean | null | undefined;
}) => {
  const { t } = useTranslate();
  return (
    <Tooltip
      title={
        hasAccessToGenerator
          ? t(
              'As a Curipod school plan subscriber, you get this for free.',
              'As a Curipod school plan subscriber, you get this for free.',
            )
          : t(
              'Curipod school plan subscribers get this for free.',
              'Curipod school plan subscribers get this for free.',
            )
      }
      mouseEnterDelay={0.5}
    >
      <IconWrapper
        width="15px"
        height="15px"
        style={{
          color: hasAccessToGenerator ? theme.colors.black : theme.colors.yellow,
        }}
      >
        {(isEnterprisePlanGenerator || isPremiumPlanGenerator) && <EnterprisePlanIcon />}
      </IconWrapper>
    </Tooltip>
  );
};

const PositioningWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 17px;
`;

const CategoryButton = styled(Button)<{ $isComingSoon: boolean }>`
  color: ${theme.colors.black};
  background-color: ${theme.colors.paleBeige};
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-radius: 30px;
  width: 250px;
  position: relative !important;

  span {
    font-size: 21px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.white};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    color: ${theme.colors.black};
  }

  ${props =>
    props.$isComingSoon &&
    css`
      color: ${theme.colors.black};
      background-color: ${theme.colors.paleBeige};
      box-shadow: ${theme.effects.magicFloatingShadow};

      &:hover,
      &:focus,
      &:active {
        color: ${theme.colors.black};
        background-color: ${theme.colors.paleBeige};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  @media (max-width: 700px) {
    width: 220px;
    span {
      font-size: 18px;
    }
  }
`;

const SelectUploadOrGenerate = ({
  handleSelectedPageChange,
  handleIsUploadChange,
  hasUploadOption,
}: {
  handleSelectedPageChange: (page: number) => void;
  handleIsUploadChange: (isUpload: boolean) => void;
  hasUploadOption: boolean;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  return (
    <FormContainer
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'center',
      }}
    >
      <AnimatedButton
        buttonText={t('Use my materials', 'Use my materials')}
        handleSelectUpload={() => {
          metrics.logEvent('TestPrep.UploadOwnMaterials.Clicked');
          handleIsUploadChange(true);
        }}
        handleSelectedPageChange={handleSelectedPageChange}
        isEnterprisePlan={true}
        disabled={!hasUploadOption}
      />
      <AnimatedButton
        buttonText={t('Create with AI', 'Create with AI')}
        handleSelectUpload={() => {
          metrics.logEvent('TestPrep.CreateWithAI.Clicked');
          handleIsUploadChange(false);
        }}
        handleSelectedPageChange={handleSelectedPageChange}
      />
    </FormContainer>
  );
};

const useSinglePromptinputSuggestion = ({
  promptInput,
  promptInputSuggestions = [],
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  value?: string | number | { filePath: string; fileType?: string };
}) => {
  const [suggestionToRender, setSuggestionToRender] = useState<string | undefined>(
    undefined,
  );
  const intervalRef = useRef<NodeJS.Timeout>();
  const [renderedSuggestion, setRenderedSuggestion] = useState<string>('');
  const intervalTime = useMemo(() => {
    if (suggestionToRender && suggestionToRender?.length > 10) {
      return Math.max(1000 / suggestionToRender.length, 10);
    }
    return 100;
  }, [suggestionToRender]);

  // when the prompt input changes, we need to change the suggestion
  useEffect(() => {
    // if the prompt input is not short or long text, we don't need to render the placeholder
    if (promptInput.type !== 'shortText' && promptInput.type !== 'longText') return;

    // if the prompt input has a value, we don't need to render the placeholder
    if (value) return;

    const newSuggestion = promptInputSuggestions?.find(p => p.id === promptInput.id);
    if (newSuggestion) {
      setRenderedSuggestion('');
      setSuggestionToRender(newSuggestion.value);
      return;
    }

    // if there is no suggestion, we remove the placeholder
    if (promptInputSuggestions && promptInputSuggestions.length > 0) {
      setRenderedSuggestion('');
      setSuggestionToRender(undefined);
      return;
    }
  }, [promptInput, promptInputSuggestions, value]);

  // when the suggestion changes, we need to create the dynamic writing experience and thus render the placeholder
  useEffect(() => {
    if (intervalRef?.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      // this logic makes the rendered placeholder string look likes its being typed
      if (suggestionToRender) {
        setRenderedSuggestion(e => {
          return suggestionToRender?.slice(0, e.length + 1);
        });
      }
    }, intervalTime);

    return () => {
      if (intervalRef?.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intervalTime, renderedSuggestion, suggestionToRender]);
  return renderedSuggestion;
};

const TestPrepInputElement = ({
  promptInput,
  onChange,
  promptInputSuggestions,
  value,
  promptPaywall,
  userAccessBadge,
  isReady,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  onChange: (val: number | string | { filePath: string; fileType?: string }) => void;
  value?: string | number | { filePath: string; fileType?: string };
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  userAccessBadge: UserAccessBadge | undefined;
  isReady: boolean;
}) => {
  const { t } = useTranslate();
  const [passageValue, setPassageValue] = useState<
    { filePath: string; fileType?: string } | undefined
  >(value as { filePath: string; fileType: string } | undefined);

  const handleFileUploaded = (uploadedFile: { filePath: string; fileType?: string }) => {
    onChange({ filePath: uploadedFile.filePath, fileType: uploadedFile.fileType });
  };
  const renderedSuggestion = useSinglePromptinputSuggestion({
    promptInput,
    promptInputSuggestions: promptInputSuggestions,
    value,
  });

  if (promptInput.type === 'shortText') {
    return (
      <div>
        <Input
          style={{ width: '100%' }}
          type="text"
          borderLess
          inputStyle={{
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          value={value as string | undefined}
          label={promptInput.label}
          placeholder={
            (renderedSuggestion as string) ||
            t('Add your {{promptInputLabel}}', 'Add your {{promptInputLabel}}', {
              promptInputLabel: promptInput.label,
            })
          }
          labelStyle={{
            fontSize: '16px',
            color: theme.colors.black,
            fontWeight: 'bold',
          }}
          maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT}
          error={
            value &&
            (value as string).length >=
              (promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT)
              ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                  MAX_LIMIT_PROMPT}`
              : undefined
          }
          onChange={e => onChange(e.target.value)}
        />
      </div>
    );
  }
  if (promptInput.type === 'longText') {
    return (
      <TextAreaV2
        label={promptInput.label}
        labelStyle={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: theme.colors.black,
        }}
        rows={3}
        placeholder={renderedSuggestion || 'Add your ' + promptInput.label}
        symbolCount={true}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE}
        value={value as string | undefined}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_LEARNING_OBJECTIVE}`
            : undefined
        }
        textAreaStyled={{
          minHeight: '100px',
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'dropdown') {
    return (
      <TestPrepFloatingSelect
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e as string | number)}
        promptPaywall={promptPaywall}
        userAccessBadge={userAccessBadge}
        isReady={isReady}
      />
    );
  }

  if (promptInput.type === 'passage') {
    return (
      <UploadPassageInput
        onFileUploaded={handleFileUploaded}
        uploadedFile={value as { filePath: string; fileType: string } | undefined}
        onChange={onChange}
        label={promptInput.label}
        promptInput={promptInput}
        passageValue={passageValue}
        setPassageValue={setPassageValue}
        labelStyle={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: theme.colors.black,
        }}
        style={{
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
          background: theme.colors.white,
          minHeight: '100px',
          paddingBottom: '40px',
        }}
      />
    );
  }
  if (promptInput.type == 'slidesUpload') {
    return (
      <TestPrepInputFileElement
        onChange={onChange}
        label={promptInput.label}
        value={value as { filePath: string; fileType: string } | undefined}
      />
    );
  }

  return null;
};

const usePromptInputSuggestions = ({
  inputSuggestions,
  formState,
  promptInputs,
}: {
  inputSuggestions?: PromptInput[][];
  formState: GeneratorFormState | undefined;
  promptInputs: PromptInput[];
}) => {
  /**
   * used for finding the current input suggestion to render
   * */

  const intervalRef = useRef<NodeJS.Timeout>();
  const [selectedInputSuggestionIndex, setSelectedInputSuggestionIndex] = useState(0);
  // makes a interval that changes the placeholder every 4 seconds
  useEffect(() => {
    if (!inputSuggestions) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // if we have filled in all the text inputs, we don't need to render the placeholder
    if (formState) {
      const textPromptInputs = promptInputs.filter(
        e => e.type === 'shortText' || e.type === 'longText',
      );
      if (textPromptInputs.every(p => formState[p.id])) {
        return;
      }
    }

    intervalRef.current = setInterval(() => {
      setSelectedInputSuggestionIndex(i => (i + 1) % inputSuggestions.length);
    }, 3000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedInputSuggestionIndex, inputSuggestions, formState, promptInputs]);

  const currentInputSuggestions = useMemo(() => {
    if (!inputSuggestions) return [];
    return inputSuggestions[selectedInputSuggestionIndex];
  }, [inputSuggestions, selectedInputSuggestionIndex]);

  return currentInputSuggestions;
};

const SingleGeneratorForm = ({
  promptInputs,
  handleInputChange,
  handleMultipleInputsChange,
  handleSubmit,
  isLoading,
  isError,
  error,
  formState,
  promptInputSuggestions,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  preFillSearch,
  preFillGrade,
  promptPaywall,
  isReady,
  userAccessBadge,
}: {
  promptInputs: PromptInput[];
  promptInputSuggestions?: PromptInput[][];
  handleInputChange: (
    inputId: string,
    value: string | number | { filePath: string; fileType?: string },
  ) => void;
  handleMultipleInputsChange?: (
    inputsToChange: {
      inputId: string;
      value: string | number | { filePath: string; fileType: string };
    }[],
  ) => void;
  handleSubmit: () => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  formState?: GeneratorFormState;
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  preFillSearch?: string;
  preFillGrade?: string;
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
}) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const metrics = useMetrics();
  const { showNewMessages } = useIntercom();
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generatorId,
  );
  const requiredInputs = useMemo(() => promptInputs.filter(p => p.isRequired), [
    promptInputs,
  ]);

  const optionalInputs = useMemo(() => promptInputs.filter(p => !p.isRequired), [
    promptInputs,
  ]);
  const currentPromptInputSuggestions = usePromptInputSuggestions({
    inputSuggestions: promptInputSuggestions,
    formState,
    promptInputs,
  });

  const [isFormErrorVisible, setIsFormErrorVisible] = useState(false);

  useEffect(() => {
    if (isError) {
      metrics.logEvent('TestPrep.GeneratorError', {
        generatorId: generatorId,
        error: error?.message,
      });
      setShowErrorModal(true);
    }
  }, [error, generatorId, isError, metrics]);

  //Pre fill form with search and or grade
  useEffect(() => {
    if (preFillSearch && !preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));

      if (topicInput) {
        handleInputChange(topicInput.id, preFillSearch);
      }

      return;
    }
    if (!preFillSearch && preFillGrade) {
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));
      if (gradeInput) {
        handleInputChange(gradeInput.id, preFillGrade);
      }

      return;
    }

    if (preFillSearch && preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));

      if (topicInput && gradeInput && handleMultipleInputsChange) {
        handleMultipleInputsChange([
          { inputId: topicInput.id, value: preFillSearch },
          { inputId: gradeInput.id, value: preFillGrade },
        ]);
      }
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preFillSearch, preFillGrade]);

  const isFormEnabled = useMemo(() => {
    if (formState) {
      let errorMessage = '';
      const isEnabled = requiredInputs.every(input => {
        const realValue = formState?.[input.id];
        if (!realValue || realValue === '') {
          errorMessage = `Don't forget to fill out: ${input.label}`;
        }
        return realValue && realValue !== '';
      });
      return { isEnabled, errorMessage };
    }
    return { isEnabled: false, errorMessage: 'Make sure you fill out all the boxes' };
  }, [requiredInputs, formState]);

  return (
    <>
      {authGuardComponent}
      <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage style={{ fontWeight: 800 }}>
            Uh-oh! Something went wrong on our end
          </ErrorMessage>

          <ErrorMessage style={{ fontSize: '16px' }}>
            {error?.customMessage ||
              'Please try again or contact support if the issue persists'}
          </ErrorMessage>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button width="120px" onClick={() => setShowErrorModal(false)}>
              Ok
            </Button>
            <Button
              width="120px"
              onClick={() => {
                const errorMessage = error?.customMessage || 'Unknown error occurred';
                showNewMessages(
                  `I'm having issues with lesson generation and need help.\n\nError: ${errorMessage}`,
                );
                setShowErrorModal(false);
              }}
            >
              Get Help
            </Button>
          </div>
        </div>
      </BaseModal>
      <GridContainer>
        {requiredInputs?.map(promptInput => (
          <TestPrepInputElement
            key={promptInput.id}
            promptInput={promptInput}
            promptInputSuggestions={currentPromptInputSuggestions}
            value={formState?.[promptInput.id]}
            onChange={val => handleInputChange(promptInput.id, val)}
            promptPaywall={promptPaywall}
            userAccessBadge={userAccessBadge}
            isReady={isReady}
          />
        ))}

        {optionalInputs.length > 0 && (
          <OptionalInputCollapse ghost style={{ width: '100%' }}>
            <OptionalInputCollapsePanel
              header={'Extra details (optional)'}
              key="optional"
              style={{ color: theme.colors.black, fontWeight: 'bold' }}
            >
              <OptionalGridContainer>
                {optionalInputs?.map(promptInput => (
                  <TestPrepInputElement
                    key={promptInput.id}
                    value={formState?.[promptInput.id]}
                    promptInputSuggestions={currentPromptInputSuggestions}
                    promptInput={promptInput}
                    onChange={val => handleInputChange(promptInput.id, val)}
                    promptPaywall={promptPaywall}
                    userAccessBadge={userAccessBadge}
                    isReady={isReady}
                  />
                ))}
              </OptionalGridContainer>
            </OptionalInputCollapsePanel>
          </OptionalInputCollapse>
        )}
      </GridContainer>
      {isFormErrorVisible && !isFormEnabled.isEnabled && (
        <FormErrorText>{isFormEnabled.errorMessage}</FormErrorText>
      )}
      {isReady &&
      generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise ? (
        <OrangeButton
          type="primary"
          loading={isLoading}
          $disabled={!isFormEnabled.isEnabled}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isFormEnabled.isEnabled) {
                  setIsFormErrorVisible(true);
                  return;
                }
                if (
                  generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account test prep',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconWrapper height="30px" width="30px">
              <CuripodAIStarsSvg />
            </IconWrapper>

            <span>Generate Lesson</span>
          </div>
        </OrangeButton>
      ) : (
        <OrangeButton
          type="primary"
          loading={isLoading}
          $disabled={!isFormEnabled.isEnabled}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isFormEnabled.isEnabled) {
                  setIsFormErrorVisible(true);
                  return;
                }

                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  isPremiumGenerator &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.premium &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account test prep',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconWrapper height="30px" width="30px">
              <CuripodAIStarsSvg />
            </IconWrapper>

            <span>Generate Lesson</span>
          </div>
        </OrangeButton>
      )}
    </>
  );
};

const GridContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 30px;
  grid-template-columns: 1fr;
`;

const OptionalInputCollapsePanel = styled(Collapse.Panel)`
  background-color: transparent;
  margin-top: 10px;
`;

const OptionalInputCollapse = styled(Collapse)`
  background-color: transparent;
  .ant-collapse-header {
    padding: 0 !important;
    font-size: 16px;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

const OptionalGridContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 14px;
  grid-template-columns: 1fr;
`;

const GenerateTestPrepSecondForm = ({
  setShowLoader,
  showLoader,
  availableLessonIds,
  existingFormState,
  availableGenerators,
  promptPaywall,
  isReady,
  userAccessBadge,
  isFetchingGenerators,
  testPrepState,
  selectedGenerator,
  dispatch,
}: {
  setShowLoader: (d: boolean) => void;
  showLoader: boolean;
  availableLessonIds: string[] | undefined;
  existingFormState?: GeneratorFormState | undefined;
  availableGenerators: ConsumerGenerator[] | undefined;
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
  isFetchingGenerators: boolean;
  testPrepState: TestPrepState;
  selectedGenerator: ConsumerGenerator | undefined;
  dispatch: React.Dispatch<TestPrepAction>;
}) => {
  const { tenantId, router } = useRouterMatch();
  const {
    selectedState,
    selectedTest,
    selectedTestCategory,
    lessonVariant,
    selectedQuestionType,
    isUpload,
  } = testPrepState;
  const {
    consumeGenerator,
    isError,
    isLoading,
    data,
    error,
  } = useConsumeGeneratorAsNewPresentation(tenantId, true);
  const metrics = useMetrics();
  const presetState = userAccessBadge?.organization?.region;
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const selectedStandard = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedStandard,
  );
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);
  const {
    handleInputChange,
    handleMultipleInputsChange,
    formState,
  } = useGeneratorFormState(
    selectedGenerator?.aiContent?.promptInputs,
    existingFormState,
  );

  useEffect(() => {
    if (isLoading || data) {
      setShowLoader(true);
      return;
    }

    setShowLoader(false);
    return;
  }, [isLoading, setShowLoader, data]);

  useEffect(() => {
    if (data) {
      metrics.logEvent('TestPrep.Generator.Consume.TestPrepLessonSuccessRedirect', {
        generatorId: selectedGenerator?.id,
        generatorName: selectedGenerator?.title,
        standard: selectedStandard?.id,
        subject: data.metadata?.subject,
        grade: grade,
        generatedLessonId: data.id,
        tenantSubjects: tenantSubjects,
        tenantGrades: tenantGrades,
        schoolState: presetState,
        prompt: [prompt || ''].join('\n\n'),
      });

      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, tenantId, data]);

  const isLargerThan850 = useBreakPoint(850);
  const handleGetLesson = () => {
    const [generatorId, variantId] = selectedGenerator?.id?.split('::') || [];
    metrics.logEvent('TestPrep.GetLessonButton.Clicked', {
      generatorId: variantId ? `${generatorId}::${variantId}` : generatorId,
      generatorName: variantId ? variantId : selectedGenerator?.title,
      testId: selectedTest,
      questionType: selectedQuestionType,
      lessonVariant: lessonVariant,
    });

    if (selectedGenerator?.id && selectedGenerator?.aiContent?.promptInputs) {
      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        selectedGenerator.aiContent.promptInputs,
      );

      consumeGenerator({
        generatorId: variantId ? generatorId : selectedGenerator.id || '',
        userInputFormData,
        generatorName: variantId ? variantId : selectedGenerator.title || '',
        ...(variantId ? { variantId } : {}),
        generatorPlan: selectedGenerator?.aiContent?.generatorPlan,
        onSuccessFunction: template => {
          localStorage.setItem(
            '@curipod_v1_recentGeneratorConsume',
            JSON.stringify({
              generatorId: selectedGenerator.id,
              formState,
            }),
          );

          localStorage.setItem(
            LS_KEY_TEST_PREP_STATE,
            JSON.stringify({
              selectedTest,
              selectedTestCategory: selectedTestCategory,
              selectedState,
            }),
          );

          // redirect to the designer
          router.push(
            `/${tenantId}/lessons/${template.id}/edit?generatorId=${selectedGenerator.id}`,
          );
        },
      });
    }
  };
  const lessonVariantsAvailable = useGetLessonVariants({
    testId: selectedTest,
    questionTypeId: selectedQuestionType,
    generators: availableGenerators,
    userAccessBadge,
    isUpload,
  });

  const shouldDisplayPassagePicker = lessonVariantsAvailable.length > 1;

  useEffect(() => {
    if (!lessonVariantsAvailable[0]) return;
    if (lessonVariantsAvailable.length > 1) {
      //if the teacher is not on the school plan, we find the first free lesson variant and select that.
      if (userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise) {
        const freeLessonVariant = lessonVariantsAvailable.find(
          lessonVariant => lessonVariant.isEnterprise === false,
        );
        if (freeLessonVariant) {
          dispatch({ type: 'SET_LESSON_VARIANT', payload: freeLessonVariant.value });
          return;
        }
        return;
      }
      dispatch({ type: 'SET_LESSON_VARIANT', payload: lessonVariantsAvailable[0].value });
      return;
    }
  }, [
    availableGenerators,
    dispatch,
    isUpload,
    lessonVariantsAvailable,
    selectedQuestionType,
    selectedTest,
    userAccessBadge,
  ]);

  const lessonVariantOptions = useGetLessonVariants({
    testId: selectedTest,
    questionTypeId: selectedQuestionType,
    generators: availableGenerators,
    userAccessBadge,
    isUpload,
  });

  return (
    <>
      {showLoader && (
        <LoaderContainer>
          <ShowLessonCreationProgressBar />
        </LoaderContainer>
      )}
      {!showLoader && shouldDisplayPassagePicker && (
        <TestPrepFloatingSelect
          label={
            selectedQuestionType === 'ECR' ||
            selectedQuestionType === 'PT' ||
            selectedQuestionType === 'essay'
              ? 'Composition Type'
              : 'Passage Genre'
          }
          width={isLargerThan850 ? undefined : '100%'}
          value={lessonVariant}
          options={lessonVariantOptions}
          onSelect={lessonVariant => {
            dispatch({ type: 'SET_LESSON_VARIANT', payload: lessonVariant });
          }}
          promptPaywall={promptPaywall}
          userAccessBadge={userAccessBadge}
          isReady={isReady}
        />
      )}
      {!showLoader &&
        availableLessonIds &&
        availableLessonIds[0] &&
        selectedGenerator &&
        !isFetchingGenerators && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              margin: '20px 0px',
              gap: 20,
            }}
          >
            <SingleGeneratorForm
              promptInputs={selectedGenerator?.aiContent?.promptInputs || []}
              isError={isError}
              error={error}
              promptInputSuggestions={
                selectedGenerator?.aiContent?.promptInputSuggestions || []
              }
              isPremiumGenerator={selectedGenerator?.aiContent?.isPremiumGenerator}
              generatorPlan={selectedGenerator?.aiContent?.generatorPlan}
              generatorId={selectedGenerator?.id}
              formState={formState}
              handleInputChange={handleInputChange}
              handleMultipleInputsChange={handleMultipleInputsChange}
              handleSubmit={handleGetLesson}
              promptPaywall={promptPaywall}
              isReady={isReady}
              userAccessBadge={userAccessBadge}
            />
          </div>
        )}
      {!showLoader && isFetchingGenerators && (
        <LoaderContainer>
          <Spin
            indicator={
              <LoadingOutlined
                style={{ marginTop: 30, fontSize: 60, color: theme.colors.orange }}
                spin
              />
            }
          />
        </LoaderContainer>
      )}
      {!selectedGenerator && availableLessonIds?.length === 1 && !isFetchingGenerators && (
        <div
          style={{
            margin: '20px 0px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SorrySvg />
          <h2 style={{ textAlign: 'center' }}>
            Uh-oh! Something didn&apos;t go as planned.
          </h2>
          <p style={{ textAlign: 'center', maxWidth: 500 }}>
            We&apos;ve been notified of the issue, but in the meantime, you could try a
            different test. If you&apos;re still having trouble, just pop us a message
            using the chat in the bottom right, we&apos;re here to help!
          </p>
        </div>
      )}
    </>
  );
};

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const FormErrorText = styled.p`
  color: ${theme.colors.red};
  font-size: 14px;
  margin: 0;
  margin-top: 10px;
  text-align: center;
  background-color: ${theme.colors.veryLightRed};
  border-radius: 10px;
  padding: 10px;
`;

const OrangeButton = styled(Button)<{ $disabled: boolean }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  box-shadow: ${theme.effects.magicFloatingShadow};
  margin: auto;

  svg {
    height: 25px;
    width: 25px;
    margin-right: 0px;
  }

  ${props =>
    props.$disabled &&
    css`
      background-color: ${theme.colors.orangeDark};
      box-shadow: ${theme.effects.magicFloatingShadow};
      &:hover {
        background-color: ${theme.colors.orangeDark};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  &:hover {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
  }

  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

const FormContainer = styled.div`
  padding-top: 10px;
  margin: 20px auto;
  width: 100%;
  max-width: 800px;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const TabContainer = styled.div`
  padding-top: 0px;
  margin: 0px auto 50px auto;
  width: 80%;
  max-width: 800px;
  display: grid;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (max-width: 700px) {
    padding-top: 0px;
  }
`;

const TestPrepInputFileElement = ({
  onChange,
  value,
  label,
}: {
  value: { filePath: string; fileType: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  label: string;
}) => {
  const {
    fileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingFile,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingFile,
    reset: resetFileUpload,
  } = useUploadPresentation();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<
    | {
        filePath: string;
        fileType: string;
      }
    | undefined
  >(value);
  const handleFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && fileInfo) {
      setUploadedFile(fileInfo);
      resetFileUpload();
      onChange(fileInfo);
    }
  }, [fileInfo, onChange, uploadPresentationSuccess, resetFileUpload]);
  return (
    <TestPrepFileUploadArea
      handleFileSelected={handleFileSelected}
      isUploadingFile={isUploadingFile}
      uploadedFile={uploadedFile}
      setUploadedFile={setUploadedFile}
      isErrorUploadingFile={isErrorUploadingFile}
      resetFileUpload={resetFileUpload}
      fileSizeExceeded={fileSizeExceeded}
      label={label}
    />
  );
};
const TestPrepFileUploadArea = ({
  uploadedFile,
  setUploadedFile,
  isUploadingFile,
  isErrorUploadingFile,
  handleFileSelected,
  resetFileUpload,
  fileSizeExceeded,
  label = 'Upload Slides',
}: {
  uploadedFile: { filePath: string; fileType: string } | undefined;
  setUploadedFile: (
    uploadedFile:
      | {
          filePath: string;
          fileType: string;
        }
      | undefined,
  ) => void;
  isUploadingFile: boolean;
  isErrorUploadingFile: boolean;
  handleFileSelected: (file: File) => void;
  resetFileUpload: () => void;
  fileSizeExceeded: boolean;
  label?: string;
}) => {
  const [fileName, setFileName] = useState<string | undefined>();

  useEffect(() => {
    if (uploadedFile) {
      //Cut off the first section of the file path to give the name of the file
      const index = uploadedFile.filePath?.indexOf('_');

      if (index !== -1) {
        const findFileName = uploadedFile.filePath.substring(index + 1);
        setFileName(findFileName);
      }
    }
  }, [uploadedFile]);

  return (
    <>
      {!(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            width: '100%',
          }}
        >
          <p
            style={{
              fontSize: 16,
              marginBottom: 4,
              color: theme.colors.black,
              fontWeight: 'bold',
            }}
          >
            {label}
          </p>

          {fileSizeExceeded && (
            <ErrorMessage style={{ fontSize: 12, textAlign: 'left' }}>
              Oops looks like this presentation is too big. Please try uploading a file
              smaller than 100MB
            </ErrorMessage>
          )}
          <TestPrepPresentationFileSelector onFileSelected={handleFileSelected} />
        </div>
      )}
      {(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            alignItems: 'space-beetween',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isUploadingFile && (
            <div
              style={{
                gridColumn: '1/3',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Loader />
              <h3
                style={{ width: '100%', textAlign: 'center', color: theme.colors.black }}
              >
                Uploading
              </h3>
            </div>
          )}
          {isErrorUploadingFile && (
            <div
              style={{
                width: '100%',
              }}
            >
              <IconWrapper height="100px" width="100px">
                <FoldedHandsSVG />
              </IconWrapper>
              <h3 style={{ width: '100%', textAlign: 'center' }}>Failed to upload</h3>
              <p style={{ textAlign: 'center' }}>
                Make sure the file is correct format, not corrupted and there is no
                password protection
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    resetFileUpload();
                    setUploadedFile(undefined);
                  }}
                >
                  Please try again
                </Button>
              </div>
            </div>
          )}
          {uploadedFile && (
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 5,
                alignItems: 'center',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  wordBreak: 'break-all',
                  color: theme.colors.black,
                }}
              >
                {fileName}{' '}
                <span style={{ fontSize: 10, display: 'block' }}>
                  successfully uploaded
                </span>
              </h3>
              <Button type="ghost" onClick={() => setUploadedFile(undefined)}>
                Change
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const validFileTypes = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', //pptx
  'key',
  'application/pdf',
];

const TestPrepPresentationFileSelector = (props: {
  onFileSelected: (file: File) => void;
  metricsName?: string;
}) => {
  const { onFileSelected, metricsName } = props;

  const metrics = useMetrics();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      onFileSelected(files[0]);
    }
  };

  const handleActivate = () => {
    metrics.logEvent(metricsName || 'selectFile');

    if (ref.current) {
      ref.current.value = '';
      ref.current!.click();
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  const handleFilesDropped = (
    files: FileList | null,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !validFileTypes.includes(file.type) && !file.name.endsWith('.key'),
      );
      if (invalidFiles.length > 0) {
        alert('Invalid file type, please try again');
        return;
      }

      if (parsedFiles.length > 0) {
        metrics.logEvent('TestPrep.ImportPresentation.DropFile', {
          count: parsedFiles.length,
          type: parsedFiles[0].type,
          size: parsedFiles[0].size,
          name: parsedFiles[0].name,
        });
        onFileSelected(parsedFiles[0]);
      }
    }
  };
  return (
    <>
      <input
        data-testid="FileInput"
        ref={ref}
        type="file"
        name="myFile"
        multiple={false}
        accept={isIos ? '*' : '.pptx, .key, .ppt, .pdf'}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <ChildWrapper
        data-testid="FileInputClickButton"
        tabIndex={0}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === 'space') {
            handleActivate();
          }
        }}
        onClick={handleActivate}
      >
        <TestPrepPresentationFileDrop onFilesDropped={handleFilesDropped} />
      </ChildWrapper>
    </>
  );
};

const TestPrepPresentationFileDrop = ({
  onFilesDropped,
}: {
  onFilesDropped?: (
    files: FileList | null,
    event: React.DragEvent<HTMLDivElement>,
  ) => void;
}) => {
  return (
    <Container>
      {/* @ts-ignore */}
      <FileDrop onDrop={(files, event) => onFilesDropped && onFilesDropped(files, event)}>
        <ContentContainer>
          <Title>Drag and drop or click to select a presentation</Title>
          <Description>Supported file types are .pptx, .ppt, .key, .pdf</Description>
        </ContentContainer>
      </FileDrop>
    </Container>
  );
};

const Title = styled.p`
  &&& {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0px;
    margin-top: 0px;
    text-align: center;
    user-select: none;
    color: ${theme.colors.black};
  }

  @media (max-width: 500px) {
    &&& {
      font-size: 14px;
    }
  }
`;

const Description = styled.p`
  &&& {
    font-size: 14px;
    font-weight: normal;
    margin-top: 0px;
    margin-bottom: 0px;
    text-align: center;
    user-select: none;
    color: ${theme.colors.black};
  }
  @media (max-width: 500px) {
    &&& {
      font-size: 12px;
    }
  }
`;

const ContentContainer = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 20px;
  border-radius: 10px;
  border: 2px dashed ${theme.colors.lightBlue};
  background-color: ${theme.colors.lessTranslucentWhite};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.lightBlue};
    border: 2px dashed ${theme.colors.white};
    transition: all 0.4s;
  }

  &:active {
    background-color: ${theme.colors.lightBlue};
    border: 2px dashed ${theme.colors.white};
    transition: all 0.4s;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;

  .file-drop {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .file-drop-target {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .file-drop > .file-drop-target.file-drop-dragging-over-frame > div {
    background-color: ${theme.colors.lightBlue};
    border: 2px dashed ${theme.colors.white};
    transition: all 0.4s;
  }
`;

const ChildWrapper = styled.div`
  width: inherit;
  height: inherit;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &:focus-within {
    outline: none;
  }
`;
